import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_DOCUMENTS, ADD_DOCUMENT, DELETE_DOCUMENT } from '../graphql';
import { Shield, Plus, Trash2, ExternalLink, AlertTriangle, Lock, FileText, Calendar, X, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';

const DOC_TYPES = [
  { value: 'AADHAAR',    label: 'Aadhaar Card',         emoji: '🪪', sensitive: true,  category: 'Identity' },
  { value: 'PAN',        label: 'PAN Card',             emoji: '💳', sensitive: true,  category: 'Identity' },
  { value: 'BIRTH_CERT', label: 'Birth Certificate',    emoji: '📜', sensitive: false, category: 'Identity' },
  { value: 'MARKSHEET',  label: 'Marksheet',            emoji: '📋', sensitive: false, category: 'Academic' },
  { value: 'DEGREE',     label: 'Degree Certificate',   emoji: '🎓', sensitive: false, category: 'Academic' },
  { value: 'MIGRATION',  label: 'Migration Certificate',emoji: '📄', sensitive: false, category: 'Academic' },
  { value: 'CHARACTER',  label: 'Character Certificate',emoji: '📄', sensitive: false, category: 'Academic' },
  { value: 'BONAFIDE',   label: 'Bonafide Certificate', emoji: '📄', sensitive: false, category: 'Academic' },
  { value: 'OTHER',      label: 'Other',                emoji: '📁', sensitive: false, category: 'Other' },
];

const CATEGORIES = ['All', 'Identity', 'Academic', 'Other'];

const DIGILOCKER_URL = 'https://www.digilocker.gov.in';

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ expiryDate }) {
  if (!expiryDate) return null;
  const days = daysUntil(expiryDate);
  if (days < 0) return <span className="badge bg-red-100 text-red-700">Expired</span>;
  if (days < 30) return <span className="badge bg-amber-100 text-amber-700">Expires in {days}d</span>;
  if (days < 90) return <span className="badge bg-yellow-100 text-yellow-700">Expires {new Date(expiryDate).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}</span>;
  return <span className="text-xs text-gray-400">{new Date(expiryDate).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}</span>;
}

function DocumentCard({ doc, onDelete }) {
  const [showRef, setShowRef] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const typeInfo = DOC_TYPES.find(t => t.value === doc.docType) || DOC_TYPES[DOC_TYPES.length-1];

  return (
    <div className="card !p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0"><FileText size={18} className="text-brand-600"/></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-800 text-sm truncate">{doc.title}</h3>
              {doc.isSensitive && <span className="flex items-center gap-0.5 text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full font-medium"><Lock size={10}/> Sensitive</span>}
              <ExpiryBadge expiryDate={doc.expiryDate} />
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{typeInfo.label}</p>

            {/* Masked reference */}
            {doc.referenceMasked && (
              <div className="flex items-center gap-2 mt-2">
                <p className="text-xs font-mono text-gray-500 tracking-wider">
                  {showRef ? doc.referenceMasked : '●●●●  ●●●●  ●●●●'}
                </p>
                <button onClick={() => setShowRef(s=>!s)} className="text-gray-300 hover:text-gray-500">
                  {showRef ? <EyeOff size={12}/> : <Eye size={12}/>}
                </button>
              </div>
            )}

            <div className="flex items-center gap-3 mt-2">
              {doc.issuer && <span className="text-xs text-gray-400">📍 {doc.issuer}</span>}
              {doc.issueDate && <span className="text-xs text-gray-400">📅 {doc.issueDate}</span>}
            </div>
            {doc.notes && <p className="text-xs text-gray-400 mt-1 italic">{doc.notes}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {doc.fileUrl && (
            <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-indigo-600 hover:underline">
              <ExternalLink size={12}/> Open
            </a>
          )}
          {!confirmDel
            ? <button onClick={() => setConfirmDel(true)} className="text-gray-300 hover:text-red-400"><Trash2 size={14}/></button>
            : (
              <div className="flex items-center gap-1">
                <span className="text-xs text-red-500">Delete?</span>
                <button onClick={() => { onDelete(doc.id); setConfirmDel(false); }} className="text-xs text-red-600 font-bold hover:underline">Yes</button>
                <button onClick={() => setConfirmDel(false)} className="text-xs text-gray-400 hover:underline">No</button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function AddDocumentForm({ onClose, onSuccess }) {
  const [addDoc, { loading }] = useMutation(ADD_DOCUMENT, {
    refetchQueries: [MY_DOCUMENTS],
    onCompleted: onSuccess,
  });
  const [form, setForm] = useState({ docType: 'MARKSHEET', title: '', issuer: '', issueDate: '', expiryDate: '', referenceMasked: '', fileUrl: '', notes: '' });
  const up = (k, v) => setForm(f => ({...f, [k]: v}));

  const typeInfo = DOC_TYPES.find(t => t.value === form.docType);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addDoc({
      variables: {
        docType: form.docType,
        title: form.title,
        issuer: form.issuer || null,
        issueDate: form.issueDate || null,
        expiryDate: form.expiryDate || null,
        referenceMasked: form.referenceMasked || null,
        fileUrl: form.fileUrl || null,
        notes: form.notes || null,
        isSensitive: typeInfo?.sensitive || false,
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Add Document</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
        </div>

        {/* Security notice */}
        <div className="mx-5 mt-4 bg-blue-50 border border-blue-100 rounded-xl p-3 flex gap-2">
          <Shield size={16} className="text-blue-500 flex-shrink-0 mt-0.5"/>
          <div>
            <p className="text-xs font-semibold text-blue-700">🔒 Security-First Storage</p>
            <p className="text-xs text-blue-600 mt-0.5">Only <strong>metadata</strong> is stored here — no raw document bytes. Upload your actual files to <a href={DIGILOCKER_URL} target="_blank" rel="noopener noreferrer" className="underline">DigiLocker</a> (India's official govt platform) and paste the share link below. Sensitive IDs (Aadhaar/PAN) are stored as last-4 masked numbers only.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
            <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400" value={form.docType} onChange={e=>up('docType',e.target.value)}>
              {CATEGORIES.filter(c=>c!=='All').map(cat => (
                <optgroup key={cat} label={cat}>
                  {DOC_TYPES.filter(t=>t.category===cat).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          {typeInfo?.sensitive && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 flex gap-2">
              <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5"/>
              <p className="text-xs text-red-600">This is a <strong>sensitive document</strong> (Aadhaar/PAN). Per <strong>UIDAI/NPCI guidelines</strong>, we store ONLY the last 4 digits as a masked reference. Never enter your full ID number below.</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Document Title</label>
            <input required className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400" value={form.title} onChange={e=>up('title',e.target.value)} placeholder={typeInfo?.label || 'Document title'} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Issuing Authority</label>
              <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400" value={form.issuer} onChange={e=>up('issuer',e.target.value)} placeholder="CBSE / UIDAI / University" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {typeInfo?.sensitive ? 'Last 4 digits only (masked)' : 'Reference / Roll No. (optional)'}
              </label>
              <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:border-indigo-400" value={form.referenceMasked} onChange={e=>up('referenceMasked', typeInfo?.sensitive ? e.target.value.replace(/\D/g,'').slice(-4) : e.target.value)} placeholder={typeInfo?.sensitive ? 'Last 4 digits (e.g. 3421)' : 'Optional'} maxLength={typeInfo?.sensitive ? 4 : 50} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Issue Date</label>
              <input type="date" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400" value={form.issueDate} onChange={e=>up('issueDate',e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date (if any)</label>
              <input type="date" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400" value={form.expiryDate} onChange={e=>up('expiryDate',e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">DigiLocker / External Link (optional)</label>
            <input type="url" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400" value={form.fileUrl} onChange={e=>up('fileUrl',e.target.value)} placeholder="https://www.digilocker.gov.in/share/..." />
            <p className="text-xs text-gray-400 mt-1">Paste your DigiLocker share link — actual file is stored securely by the Government of India</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
            <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400" value={form.notes} onChange={e=>up('notes',e.target.value)} placeholder="Semester 4 marksheet — 90.4%" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading || !form.title} className="btn-primary flex-1">
              {loading ? 'Adding…' : 'Add Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DocumentVaultPage() {
  const [category, setCategory] = useState('All');
  const [showAdd, setShowAdd] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const { data, loading } = useQuery(MY_DOCUMENTS);
  const [deleteDoc] = useMutation(DELETE_DOCUMENT, { refetchQueries: [MY_DOCUMENTS] });

  const docs = data?.myDocuments || [];

  const filteredDocs = category === 'All'
    ? docs
    : docs.filter(d => {
        const type = DOC_TYPES.find(t => t.value === d.docType);
        return type?.category === category;
      });

  // Expiry alerts
  const expiringSoon = docs.filter(d => { const days = daysUntil(d.expiryDate); return days !== null && days >= 0 && days <= 30; });
  const expired = docs.filter(d => { const days = daysUntil(d.expiryDate); return days !== null && days < 0; });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-xl"><Shield size={20} className="text-green-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Vault</h1>
            <p className="text-gray-500 text-sm">Secure metadata storage · Govt-guideline compliant · DigiLocker integrated</p>
          </div>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={14}/> Add Document
        </button>
      </div>

      {/* Security info banner */}
      <button onClick={() => setShowInfo(s=>!s)} className="w-full bg-green-50 border border-green-100 rounded-xl p-4 mb-5 text-left">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-green-600"/>
            <span className="text-sm font-semibold text-green-700">🔐 How your documents are protected</span>
          </div>
          {showInfo ? <ChevronUp size={14} className="text-green-500"/> : <ChevronDown size={14} className="text-green-500"/>}
        </div>
        {showInfo && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { title: 'No Raw Files Stored', desc: 'Zero document bytes stored on our servers. Only titles, dates, and metadata.' },
              { title: 'DigiLocker for Files', desc: 'Actual documents live in DigiLocker — India\'s Govt-operated secure document wallet (IT Act 2000).' },
              { title: 'Masked IDs Only', desc: 'Aadhaar/PAN numbers stored as last-4 only ("XXXX-XXXX-3421") per UIDAI & IT Act guidelines.' },
            ].map(item => (
              <div key={item.title} className="bg-white rounded-lg p-3 text-left">
                <p className="text-xs font-semibold text-gray-700 mb-0.5">{item.title}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        )}
      </button>

      {/* DigiLocker CTA */}
      <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-5">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-blue-600" />
          <div>
            <p className="text-sm font-semibold text-blue-700">Store your actual documents on DigiLocker</p>
            <p className="text-xs text-blue-500">Free · Govt of India · Accepted by banks, universities, and employers</p>
          </div>
        </div>
        <a href={DIGILOCKER_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 btn-secondary text-sm whitespace-nowrap">
          Open DigiLocker <ExternalLink size={12}/>
        </a>
      </div>

      {/* Expiry alerts */}
      {(expired.length > 0 || expiringSoon.length > 0) && (
        <div className="space-y-2 mb-5">
          {expired.map(d => (
            <div key={d.id} className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-2">
              <AlertTriangle size={14} className="text-red-500"/>
              <span className="text-sm text-red-700"><strong>{d.title}</strong> has expired — please renew</span>
            </div>
          ))}
          {expiringSoon.map(d => (
            <div key={d.id} className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2">
              <AlertTriangle size={14} className="text-amber-500"/>
              <span className="text-sm text-amber-700"><strong>{d.title}</strong> expires in {daysUntil(d.expiryDate)} days</span>
            </div>
          ))}
        </div>
      )}

      {/* Category tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-5 w-fit">
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => setCategory(cat)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${category===cat ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
            {cat}
            <span className="ml-1.5 text-xs text-gray-400">
              ({cat==='All' ? docs.length : docs.filter(d=>{const t=DOC_TYPES.find(x=>x.value===d.docType);return t?.category===cat;}).length})
            </span>
          </button>
        ))}
      </div>

      {/* Documents grid */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading vault…</div>
      ) : filteredDocs.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-5xl mb-3">📂</div>
          <p className="text-gray-500 font-medium">No documents in {category === 'All' ? 'vault' : category} yet</p>
          <p className="text-sm text-gray-400 mt-1">Click "Add Document" to track your first document</p>
          <button onClick={() => setShowAdd(true)} className="btn-primary mt-4 text-sm">+ Add Document</button>
        </div>
      ) : (
        <div className="space-y-3">
          {CATEGORIES.filter(c=>c!=='All').map(cat => {
            const catDocs = filteredDocs.filter(d => { const t = DOC_TYPES.find(x=>x.value===d.docType); return t?.category===cat; });
            if (catDocs.length === 0 && category !== 'All') return null;
            if (catDocs.length === 0) return null;
            return (
              <div key={cat}>
                {category === 'All' && <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{cat}</p>}
                <div className="space-y-2">
                  {catDocs.map(doc => (
                    <DocumentCard key={doc.id} doc={doc} onDelete={id => deleteDoc({ variables: { documentId: id } })} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stats bar */}
      {docs.length > 0 && (
        <div className="mt-6 grid grid-cols-4 gap-3">
          {[
            { label: 'Total Documents', value: docs.length, color: 'bg-indigo-50 text-indigo-700' },
            { label: 'Identity', value: docs.filter(d=>DOC_TYPES.find(t=>t.value===d.docType)?.category==='Identity').length, color: 'bg-purple-50 text-purple-700' },
            { label: 'Academic', value: docs.filter(d=>DOC_TYPES.find(t=>t.value===d.docType)?.category==='Academic').length, color: 'bg-blue-50 text-blue-700' },
            { label: 'Expiring Soon', value: expiringSoon.length + expired.length, color: expiringSoon.length+expired.length > 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-400' },
          ].map(s => (
            <div key={s.label} className={`${s.color} rounded-xl p-3 text-center`}>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAdd && <AddDocumentForm onClose={() => setShowAdd(false)} onSuccess={() => setShowAdd(false)} />}
    </div>
  );
}
